import { useState, useEffect } from 'react';
import { Stack, Container, Group, Divider, ScrollArea, Loader, Center, Alert } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import dealSchemaDefinition from '../../../../schemas/deal.schema';
import {
  useDealRepository,
  useDealStatusRepository,
  useDealTypeRepository,
} from '@ayocore/erp/crm/client/rpc';
import { useAccountingCurrencyRepository } from '@ayocore/erp/accounting/client/rpc';
import type { UpdateDeal, Deal } from '../../../../schemas/deal.schema';
import logger from '@ayocore/platform/common/helpers/logger';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect/RepositorySelect';
import { UserSelect } from '@ayocore/platform/identity/client/components';
import { PartnerSelect } from '@ayocore/erp/crm/client/components/Partners/PartnerSelect';

/**
 * EditDealForm - Deal edition form
 */
export function EditDealForm({
  dealId,
  onDealUpdated,
}: {
  dealId: number;
  onDealUpdated?: () => void;
}) {
  const dealRepository = useDealRepository();
  const dealStatusRepository = useDealStatusRepository();
  const dealTypeRepository = useDealTypeRepository();
  const currencyRepository = useAccountingCurrencyRepository();

  const [initialData, setInitialData] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const SchemaForm = useSchemaForm<UpdateDeal>(dealSchemaDefinition.updateSchema, {
    hideDescriptions: true,
  });

  // Load deal data
  useEffect(() => {
    const loadDeal = async () => {
      try {
        setLoading(true);
        const data = await dealRepository.findById(dealId);
        if (data) {
          setInitialData(data);
          SchemaForm.setInitialData(data);
        }
      } catch (error) {
        logger.error('Failed to load deal:', error);
      } finally {
        setLoading(false);
      }
    };

    if (dealId) {
      loadDeal();
    }
  }, [dealId]);

  // Check update permission
  useEffect(() => {
    const checkPermissions = async () => {
      if (!dealId) return;
      try {
        setPermissionsLoading(true);
        const result = await dealRepository.canUpdate(dealId);
        setCanEdit(result.allowed);
      } catch (error) {
        logger.error('Failed to check permissions:', error);
        setCanEdit(false);
      } finally {
        setPermissionsLoading(false);
      }
    };
    checkPermissions();
  }, [dealId]);

  const handleSubmit = async (values: UpdateDeal) => {
    try {
      const updatedDeal = await dealRepository.update(dealId, values);
      SchemaForm.setSuccess(`Сделка "${updatedDeal.number}" успешно обновлена!`);

      setTimeout(() => {
        if (onDealUpdated) {
          onDealUpdated();
        }
      }, 1500);

      return { success: true };
    } catch (error) {
      logger.error('Failed to update deal:', error);
      SchemaForm.setError('Не удалось обновить сделку');
      return { success: false };
    }
  };

  if (loading || permissionsLoading) {
    return (
      <Center p='xl'>
        <Loader size='sm' />
      </Center>
    );
  }

  if (!initialData) {
    return (
      <Container size='md' py='xl'>
        <Alert color='red' title='Ошибка'>
          Сделка не найдена или нет доступа.
        </Alert>
      </Container>
    );
  }

  return (
    <form
      onSubmit={SchemaForm.handleSubmit(handleSubmit)}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      <ScrollArea.Autosize flex={1} mih={0} scrollbarSize={0}>
        <Container size='lg' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs' pb='md'>
            <SchemaForm.Messages />

            {!canEdit && (
              <Alert color='yellow' mb='xs'>
                У вас нет прав на редактирование этой сделки.
              </Alert>
            )}

            <Group grow align='flex-start'>
              <SchemaForm.Field.number disabled={!canEdit} />

              <PartnerSelect
                value={SchemaForm.values.partner_id?.toString() || ''}
                onChange={value =>
                  SchemaForm.setFieldValue('partner_id', value ? parseInt(value) : null)
                }
                disabled={!canEdit}
              />
            </Group>

            <SchemaForm.Field.description disabled={!canEdit} />
            <Divider />

            <Group grow>
              <RepositorySelect
                repo={dealTypeRepository}
                displayField='name'
                placeholder='Выберите тип сделки...'
                label='Тип сделки'
                onChange={value =>
                  SchemaForm.setFieldValue('deal_type_id', value ? parseInt(value) : 0)
                }
                value={SchemaForm.values.deal_type_id?.toString() || ''}
                disabled={!canEdit}
              />
              <RepositorySelect
                repo={dealStatusRepository}
                displayField='name'
                placeholder='Выберите статус...'
                label='Статус'
                onChange={value =>
                  SchemaForm.setFieldValue('deal_status_id', value ? parseInt(value) : 0)
                }
                value={SchemaForm.values.deal_status_id?.toString() || ''}
                disabled={!canEdit}
              />
            </Group>

            <Divider />

            <Group grow>
              <RepositorySelect
                repo={currencyRepository}
                displayField='code'
                placeholder='Валюта...'
                label='Валюта'
                onChange={value =>
                  SchemaForm.setFieldValue('currency_id', value ? parseInt(value) : null)
                }
                value={SchemaForm.values.currency_id?.toString() || ''}
                disabled={!canEdit}
              />
              <UserSelect
                placeholder='Ответственный...'
                label='Ответственный'
                value={SchemaForm.values.deal_owner_id}
                onChange={id => SchemaForm.setFieldValue('deal_owner_id', id)}
                disabled={!canEdit}
              />
            </Group>

            <Divider />

            <Group grow>
              <SchemaForm.Field.delivery_date_plan disabled={!canEdit} />
              <SchemaForm.Field.close_date_fact disabled={!canEdit} />
            </Group>

            <Divider />

            <SchemaForm.Field.notes disabled={!canEdit} />
            <SchemaForm.Field.archived disabled={!canEdit} />
          </Stack>
        </Container>
      </ScrollArea.Autosize>

      <div
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: 'transparent',
          padding: '8px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify='flex-end'>
          <SchemaForm.buttons.cancel onClick={onDealUpdated} label='Отмена' />
          <SchemaForm.buttons.submit label='Сохранить изменения' disabled={!canEdit} />
        </Group>
      </div>
    </form>
  );
}
